import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import BrandMark from "@/components/BrandMark";
import { useLocale } from "@/lib/locale";

export default function Inbox() {
    const nav = useNavigate();
    const { t } = useLocale();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/inbox");
            setItems(data.items);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const openAndMarkRead = async (item) => {
        try {
            await api.post("/inbox/mark-read", { message_ids: [item.message_id] });
        } catch {}
        nav(`/chat/${item.conversation_id}`);
    };

    return (
        <div className="grain min-h-screen candle-halo">
            <Header />
            <main className="max-w-3xl mx-auto px-6 md:px-12 pt-20 pb-28 animate-fade-in">
                <p className="label-eyebrow mb-4">{t("inbox.eyebrow")}</p>
                <h1 className="h-serif text-5xl md:text-6xl mb-5 leading-[1.1]">
                    {t("inbox.title_a")} <span className="italic text-ink-gold">{t("inbox.title_i")}</span>
                </h1>
                <p className="text-ink-textMid text-xl max-w-xl leading-[1.6]">
                    {t("inbox.body")}
                </p>

                <div className="mt-14">
                    {loading ? (
                        <p className="text-ink-textLow font-serif italic">{t("inbox.loading")}</p>
                    ) : items.length === 0 ? (
                        <div className="border border-ink-border rounded-sm p-14 text-center" data-testid="inbox-empty">
                            <p className="font-serif italic text-3xl text-ink-textHigh">{t("inbox.empty_title")}</p>
                            <p className="text-ink-textMid text-xl mt-5 max-w-md mx-auto leading-[1.6]">
                                {t("inbox.empty_body")}
                            </p>
                            <Link to="/dashboard" className="btn-secondary mt-10 inline-block">{t("inbox.back")}</Link>
                        </div>
                    ) : (
                        <div className="divide-y divide-ink-border border-y border-ink-border" data-testid="inbox-list">
                            {items.map((it) => (
                                <button
                                    key={it.message_id}
                                    onClick={() => openAndMarkRead(it)}
                                    data-testid={`inbox-item-${it.message_id}`}
                                    className="w-full text-left py-8 px-2 flex items-start gap-6 hover:bg-ink-surface/60 transition-colors duration-300"
                                >
                                    <div className={`photo-frame w-24 h-24 md:w-28 md:h-28 rounded-full shrink-0 overflow-hidden photo-halo ${!it.is_read ? "presence-pulse" : ""}`}>
                                        {it.presence_photo ? (
                                            <img src={`data:${it.presence_photo_mime};base64,${it.presence_photo}`} alt="" className="w-full h-full object-cover grayscale-[15%]" />
                                        ) : (
                                            <div className="w-full h-full bg-ink-surfaceUp flex items-center justify-center">
                                                <BrandMark monogram size={56} className="opacity-80" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-3 flex-wrap">
                                            <span className="font-serif text-[1.6rem] text-ink-textHigh">{it.presence_name}</span>
                                            {!it.is_read && (
                                                <span className="w-2.5 h-2.5 bg-ink-gold rounded-full" aria-label="unread"></span>
                                            )}
                                            <span className="label-eyebrow">{it.relationship}</span>
                                        </div>
                                        <p className="mt-4 font-serif text-[1.3rem] text-ink-textHigh leading-[1.55] whitespace-pre-wrap">
                                            {it.content}
                                        </p>
                                        <p className="text-sm text-ink-textMid mt-4 tracking-widest uppercase">
                                            {new Date(it.created_at).toLocaleDateString()} · {t("inbox.thought_first")}
                                        </p>
                                    </div>
                                    <span className="text-ink-textMid self-center">→</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
